import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { refreshMe } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshMe();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        // keep polling — session may not be recorded yet
      }
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshMe]);

  return (
    <div className="min-h-[92vh] flex items-center justify-center px-4 sm:px-6 pt-24 pb-16 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <div>
          <Loader2 className="h-12 w-12 text-gold animate-spin mx-auto" />
          <p className="text-smoke mt-6">Confirming your payment…</p>
        </div>
      )}
      {status === "paid" && (
        <div>
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
          <h1 className="font-anton uppercase tracking-tighter text-cream text-3xl sm:text-4xl mt-6">Payment Confirmed</h1>
          <p className="text-smoke mt-3 max-w-md">You're all set. Your membership or booking is now active.</p>
          <Button
            asChild
            className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider mt-8"
            data-testid="payment-success-dashboard-button"
          >
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      )}
      {(status === "timeout" || status === "error" || status === "expired") && (
        <div>
          <XCircle className="h-16 w-16 text-red-400 mx-auto" />
          <h1 className="font-anton uppercase tracking-tighter text-cream text-3xl sm:text-4xl mt-6">Still Processing</h1>
          <p className="text-smoke mt-3 max-w-md">
            We couldn't confirm the payment yet. Check your dashboard in a moment, or contact us if the issue persists.
          </p>
          <Button asChild variant="outline" className="rounded-none border-gold text-gold hover:bg-gold hover:text-void mt-8" data-testid="payment-timeout-dashboard-button">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
